import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { toast, Toaster } from "sonner";
import { Link2, CheckCircle2, X, UserMinus } from "lucide-react";

export default function Connections() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState([]);
  const [mine, setMine] = useState([]);

  const load = () => {
    api.get("/client-connections/incoming").then(r => setIncoming(r.data.requests || [])).catch(() => {});
    api.get("/client-connections/mine").then(r => setMine(r.data.connections || [])).catch(() => {});
  };

  useEffect(() => {
    if (user === null) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "client") { navigate("/dashboard"); return; }
    load();
    // eslint-disable-next-line
  }, [user, navigate]);

  if (!user || user.role !== "client") return null;

  const respond = async (id, action) => {
    try { await api.post(`/client-connections/${id}/${action}`); toast.success(action === "accept" ? "Connected!" : "Declined"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this connection?")) return;
    try { await api.delete(`/client-connections/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div>
      <Header/>
      <Toaster position="top-center"/>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2" style={{ fontFamily: "Outfit" }} data-testid="connections-title"><Link2 size={24} className="text-[#C79B6B]"/> Connections</h1>
        <p className="text-sm text-[#222]/60 mb-8">Mutual connections with neighbors you've met through the Wall or shared boards. There's no public directory — connections start from a profile link.</p>

        {incoming.length > 0 && (
          <section className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-[#2E6B57]/40" data-testid="connections-incoming">
            <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Requests</h2>
            <div className="space-y-2">
              {incoming.map(c => (
                <div key={c.id} className="flex items-center gap-3 border border-black/5 rounded-xl p-3" data-testid={`connections-incoming-${c.id}`}>
                  <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold overflow-hidden shrink-0">
                    {c.requester.avatar_url ? <img src={c.requester.avatar_url} className="w-full h-full object-cover" alt=""/> : c.requester.name?.[0]}
                  </div>
                  <p className="flex-1 min-w-0 font-semibold text-sm truncate">{c.requester.name}</p>
                  <button onClick={() => respond(c.id, "accept")} className="text-xs btn-lime px-3 py-1.5 flex items-center gap-1" data-testid={`connections-accept-${c.id}`}><CheckCircle2 size={13}/> Accept</button>
                  <button onClick={() => respond(c.id, "decline")} className="text-xs px-3 py-1.5 rounded-full bg-black/5" data-testid={`connections-decline-${c.id}`}><X size={13}/></button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="connections-mine">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Your connections ({mine.length})</h2>
          {mine.length === 0 ? (
            <p className="text-sm text-[#222]/50">No connections yet. You'll see a Connect option on a neighbor's profile when you meet them through the Wall.</p>
          ) : (
            <div className="space-y-2">
              {mine.map(c => (
                <div key={c.id} className="flex items-center gap-3 border border-black/5 rounded-xl p-3" data-testid={`connections-mine-${c.id}`}>
                  <button onClick={() => navigate(`/client/${c.user_id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`connections-mine-open-${c.id}`}>
                    <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold overflow-hidden shrink-0">
                      {c.avatar_url ? <img src={c.avatar_url} className="w-full h-full object-cover" alt=""/> : c.name?.[0]}
                    </div>
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                  </button>
                  <button onClick={() => remove(c.id)} className="text-[#222]/30 hover:text-red-500" data-testid={`connections-remove-${c.id}`}><UserMinus size={15}/></button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
